import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Icon,
  MediaPreview,
  cn,
} from "@nexus/ui";
import type { UploadItemState } from "../../hooks/useUploadManager";
import { useObjectUrl } from "../../hooks/useObjectUrl";

const PREVIEW_CLASS =
  "size-10 shrink-0 overflow-hidden rounded-md bg-muted ring-1 ring-border/60";

interface UploadStatusOverlayProps {
  state: UploadItemState;
}

function UploadStatusOverlay({ state }: UploadStatusOverlayProps) {
  const isInFlight = state === "uploading" || state === "confirming";
  if (!isInFlight && state !== "failed") return null;

  return (
    <span
      className={cn(
        "border-background absolute -right-1 -bottom-1 flex size-4 items-center justify-center rounded-full border shadow-sm",
        isInFlight ? "bg-background text-primary" : "bg-error-subtle text-error",
      )}
      aria-hidden
    >
      <Icon
        name={isInFlight ? "loader" : "x-circle"}
        size="xs"
        className={cn(isInFlight && "animate-spin")}
      />
    </span>
  );
}

interface VideoFilePreviewProps {
  url: string;
  name: string;
  className?: string;
}

function VideoFilePreview({ url, name, className }: VideoFilePreviewProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className={cn("relative block", className)}
        onClick={() => setOpen(true)}
        aria-label={t("preflight.assetList.preview", { name })}
      >
        <video
          src={url}
          muted
          playsInline
          preload="metadata"
          className="size-full object-cover"
        />
        <span className="absolute inset-0 flex items-center justify-center bg-black/25">
          <Icon name="clapperboard" size="xs" className="text-white" />
        </span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[min(92vw,960px)] gap-0 overflow-hidden border-0 p-0" aria-describedby={undefined}>
          <DialogTitle className="sr-only">{name}</DialogTitle>
          <video
            src={url}
            controls
            autoPlay
            playsInline
            className="max-h-[80vh] w-full bg-black"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

function FallbackPreviewIcon({ file }: { file: File }) {
  const isVideo = file.type.startsWith("video/");

  return (
    <span
      className={cn(
        PREVIEW_CLASS,
        "text-muted-foreground flex items-center justify-center",
      )}
      aria-hidden
    >
      <Icon name={isVideo ? "clapperboard" : "file"} size="sm" />
    </span>
  );
}

export interface UploadAssetPreviewProps {
  file: File;
  name: string;
  state: UploadItemState;
}

/** Clickable local-file preview — image lightbox via MediaPreview, video via dialog. */
export function UploadAssetPreview({ file, name, state }: UploadAssetPreviewProps) {
  // Stub files (hydrated from store after navigation) have no content — skip preview.
  const url = useObjectUrl(file.size > 0 ? file : null);
  const isImage = file.type.startsWith("image/");
  const isVideo = file.type.startsWith("video/");

  if (!url || (!isImage && !isVideo)) {
    return (
      <div className="relative shrink-0">
        <FallbackPreviewIcon file={file} />
        <UploadStatusOverlay state={state} />
      </div>
    );
  }

  return (
    <div className="relative shrink-0">
      {isImage ? (
        <MediaPreview
          src={url}
          thumbnailSrc={url}
          type="image"
          alt={name}
          title={name}
          className={PREVIEW_CLASS}
        />
      ) : (
        <VideoFilePreview url={url} name={name} className={PREVIEW_CLASS} />
      )}
      <UploadStatusOverlay state={state} />
    </div>
  );
}
